import { useState, useMemo, useEffect } from "react";
import { useAuth, isLockedOut, getLockoutMinutes } from "../AuthContext";
import { openInternalPage } from "../utils/navigation";
import { sanitizeProfileNameInput, sanitizeEmail } from "../utils/security";

const RULES = [
  { id:"len",    label:"At least 8 characters",         test: p => p.length >= 8 },
  { id:"upper",  label:"One uppercase letter (A–Z)",    test: p => /[A-Z]/.test(p) },
  { id:"lower",  label:"One lowercase letter (a–z)",    test: p => /[a-z]/.test(p) },
  { id:"number", label:"One number (0–9)",              test: p => /[0-9]/.test(p) },
  { id:"special",label:"One special character (!@#$…)", test: p => /[^A-Za-z0-9]/.test(p) },
];

function validateUsername(u) {
  if (!u) return "Username is required.";
  if (u.length < 3) return "Username must be at least 3 characters.";
  if (u.length > 20) return "Username must be under 20 characters.";
  if (!/^[a-zA-Z0-9_]+$/.test(u)) return "Only letters, numbers and underscores allowed.";
  return "";
}

function getStrength(pass) {
  const passed = RULES.filter(r => r.test(pass)).length;
  if (pass.length === 0) return { score: 0, label: "", color: "#E2E8F0" };
  if (passed <= 1) return { score: 1, label: "Weak",      color: "#EF4444" };
  if (passed <= 2) return { score: 2, label: "Fair",      color: "#F97316" };
  if (passed <= 3) return { score: 3, label: "Good",      color: "#EAB308" };
  if (passed <= 4) return { score: 4, label: "Strong",    color: "#22C55E" };
  return              { score: 5, label: "Very Strong", color: "#10B981" };
}

export default function LoginScreen({ onChangeStorage, dark = false }) {
  const { loginGoogle, loginEmail, registerEmail, resendVerification, checkVerification, resetPassword } = useAuth();
  const [showForgot, setShowForgot]   = useState(false);
  const [resetEmail, setResetEmail]   = useState("");
  const [resetSent, setResetSent]     = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError]   = useState("");
  const [mode, setMode]           = useState("login");
  const [email, setEmail]         = useState("");
  const [pass, setPass]           = useState("");
  const [confirm, setConfirm]     = useState("");
  const [showPass, setShowPass]   = useState(false);
  const [name, setName]           = useState("");
  const [username, setUsername]   = useState("");
  const [usernameError, setUsernameError] = useState("");
  const [error, setError]         = useState("");
  const [loading, setLoading]     = useState(false);
  const [showVerify, setShowVerify] = useState(false);
  const [verifyChecking, setVerifyChecking] = useState(false);
  const [lockedOut, setLockedOut] = useState(false);
  const [lockMins, setLockMins]   = useState(0);

  const darkThemeCss = dark ? `
.login-screen[data-theme="dark"]{background:linear-gradient(160deg,#070B16 0%,#0F172A 58%,#111827 100%)!important;color:#F8FAFC!important;}
.login-screen[data-theme="dark"] [style*="background:white"],
.login-screen[data-theme="dark"] [style*="background: white"],
.login-screen[data-theme="dark"] [style*="background:#F8FAFF"],
.login-screen[data-theme="dark"] [style*="background: #F8FAFF"],
.login-screen[data-theme="dark"] [style*="background:#F1F5F9"],
.login-screen[data-theme="dark"] [style*="background: #F1F5F9"]{background:#0F172A!important;border-color:#334155!important;color:#F8FAFC!important;}
.login-screen[data-theme="dark"] [style*="color:#1E1B4B"],
.login-screen[data-theme="dark"] [style*="color: #1E1B4B"],
.login-screen[data-theme="dark"] [style*="color:#0F172A"],
.login-screen[data-theme="dark"] [style*="color: #0F172A"]{color:#F8FAFC!important;}
.login-screen[data-theme="dark"] [style*="color:#64748B"],
.login-screen[data-theme="dark"] [style*="color: #64748B"],
.login-screen[data-theme="dark"] [style*="color:#7C8DB5"],
.login-screen[data-theme="dark"] [style*="color: #7C8DB5"],
.login-screen[data-theme="dark"] [style*="color:#94A3B8"],
.login-screen[data-theme="dark"] [style*="color: #94A3B8"]{color:#CBD5E1!important;}
.login-screen[data-theme="dark"] input{background:#111827!important;color:#F8FAFC!important;border-color:#334155!important;}
.login-screen[data-theme="dark"] input::placeholder{color:#64748B!important;}
.login-screen[data-theme="dark"] button[style*="background:none"]{color:#A5B4FC!important;}
.login-screen[data-theme="dark"] [style*="background:#FEF2F2"],
.login-screen[data-theme="dark"] [style*="background: #FEF2F2"]{background:rgba(239,68,68,.16)!important;border-color:rgba(248,113,113,.35)!important;color:#FCA5A5!important;}
.login-screen[data-theme="dark"] [style*="background:#FEF3C7"],
.login-screen[data-theme="dark"] [style*="background: #FEF3C7"],
.login-screen[data-theme="dark"] [style*="background:#FFF7ED"],
.login-screen[data-theme="dark"] [style*="background: #FFF7ED"]{background:rgba(245,158,11,.16)!important;border-color:rgba(251,191,36,.35)!important;color:#FCD34D!important;}
.login-screen[data-theme="dark"] [style*="background:#DCFCE7"],
.login-screen[data-theme="dark"] [style*="background: #DCFCE7"]{background:rgba(16,185,129,.16)!important;border-color:rgba(52,211,153,.35)!important;color:#86EFAC!important;}
` : "";

  // ── FEATURE 2: Check per-email lockout on mount/email change ─
  useEffect(() => {
    const check = () => {
      if (mode === "login" && email && isLockedOut(email)) {
        setLockedOut(true);
        setLockMins(getLockoutMinutes(email));
      } else {
        setLockedOut(false);
        setLockMins(0);
      }
    };
    check();
    const interval = setInterval(check, 30000);
    return () => clearInterval(interval);
  }, [email, mode]);

  const strength   = useMemo(() => getStrength(pass), [pass]);
  const rulesDone  = useMemo(() => RULES.map(r => ({ ...r, passed: r.test(pass) })), [pass]);
  const allRulesPass = rulesDone.every(r => r.passed);

  const handle = async () => {
    setError("");
    if (lockedOut) { setError(`This email is temporarily locked. Try again in ${lockMins} minutes.`); return; }
    if (mode === "register") {
      if (!name.trim()) { setError("Please enter your name."); return; }
      const uErr = validateUsername(username);
      if (uErr) { setError(uErr); return; }
      if (!allRulesPass) { setError("Please meet all password requirements."); return; }
      if (pass !== confirm) { setError("Passwords do not match."); return; }
    }
    setLoading(true);
    try {
      if (mode === "login") {
        await loginEmail(email, pass);
      } else {
        await registerEmail(email, pass, name, username);
        setShowVerify(true);
      }
    } catch (e) {
      if (e.message === "EMAIL_NOT_VERIFIED") {
        setShowVerify(true);
        setError("Please verify your email first. A new link has been sent to your inbox.");
      } else if (e.message === "USERNAME_TAKEN") {
        setError("❌ This username is already taken. Please choose a different one.");
      } else if (e.message === "USERNAME_CHECK_UNAVAILABLE") {
        setError("Username availability could not be checked. Please verify your connection and try again.");
      } else {
        // Clean up Firebase error messages
        let msg = e.message.replace("Firebase: ", "").replace(/\(auth[^)]*\)\.?/g, "").trim();
        if (msg.includes("email-already-in-use")) msg = "This email is already registered. Please sign in instead.";
        if (msg.includes("invalid-email")) msg = "Please enter a valid email address.";
        if (msg.includes("network-request-failed")) msg = "Network error. Please check your connection.";
        setError(msg);
        if (isLockedOut(email)) { setLockedOut(true); setLockMins(getLockoutMinutes(email)); }
      }
    }
    setLoading(false);
  };

  const handleGoogle = async () => {
    setError(""); setLoading(true);
    try { await loginGoogle(); }
    catch (e) { setError(e.message); }
    setLoading(false);
  };

  // ── Forgot Password Screen ───────────────────────────────────
  if (showForgot) {
    return (
      <div className="login-screen" data-theme={dark ? "dark" : "light"} style={{minHeight:"100vh",background:"linear-gradient(160deg,#EEF2FF,#F0FDF4)",
        display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
        padding:24,fontFamily:"'Nunito',sans-serif"}}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap');${darkThemeCss}`}</style>

        <div style={{fontSize:56,marginBottom:12}}>🔑</div>
        <div style={{fontSize:22,fontWeight:900,color:"#1E1B4B",marginBottom:6,textAlign:"center"}}>
          Reset Password
        </div>
        <div style={{fontSize:13,color:"#7C8DB5",marginBottom:28,textAlign:"center",maxWidth:300,lineHeight:1.6}}>
          Enter your email and we'll send you a reset link
        </div>

        <div style={{background:"white",borderRadius:24,padding:"24px",width:"100%",
          maxWidth:380,boxShadow:"0 4px 32px rgba(0,0,0,0.08)"}}>

          {!resetSent ? <>
            {/* Attempts warning */}
            {(() => {
              const attempts = parseInt(localStorage.getItem("finvault_reset_attempts")||"0");
              const lockUntil = parseInt(localStorage.getItem("finvault_reset_lock")||"0");
              const isLocked = Date.now() < lockUntil;
              const minsLeft = isLocked ? Math.ceil((lockUntil-Date.now())/60000) : 0;
              return isLocked ? (
                <div style={{background:"#FEF2F2",border:"1.5px solid #FECACA",borderRadius:12,
                  padding:"12px 14px",marginBottom:16,fontSize:13,color:"#DC2626",fontWeight:700,textAlign:"center"}}>
                  🔒 Reset locked for {minsLeft} more minute{minsLeft!==1?"s":""}.<br/>
                  <span style={{fontSize:11,fontWeight:600}}>Too many failed attempts.</span>
                </div>
              ) : attempts > 0 ? (
                <div style={{background:"#FEF3C7",border:"1.5px solid #FCD34D",borderRadius:12,
                  padding:"10px 14px",marginBottom:12,fontSize:12,color:"#92400E",fontWeight:700}}>
                  ⚠️ {3-attempts} attempt{3-attempts!==1?"s":""} remaining before lockout
                </div>
              ) : null;
            })()}

            {/* Email input */}
            <div style={{marginBottom:16}}>
              <div style={{fontSize:11,fontWeight:800,color:"#94A3B8",letterSpacing:1,
                textTransform:"uppercase",marginBottom:6}}>Email Address</div>
              <input type="email" placeholder="your@email.com"
                value={resetEmail} onChange={e=>{setResetEmail(sanitizeEmail(e.target.value));setResetError("");}}
                onKeyDown={async e=>{
                  if(e.key!=="Enter") return;
                  const lockUntil = parseInt(localStorage.getItem("finvault_reset_lock")||"0");
                  if (Date.now() < lockUntil) return;
                  setResetLoading(true); setResetError("");
                  try { await resetPassword(resetEmail); setResetSent(true); }
                  catch(e2) { setResetError(e2.message); }
                  setResetLoading(false);
                }}
                style={{width:"100%",padding:"13px 16px",borderRadius:12,
                  border:`2px solid ${resetError?"#EF4444":"#E8EDFF"}`,
                  background:"#F8FAFF",fontSize:14,fontFamily:"inherit",
                  fontWeight:600,color:"#1E1B4B",outline:"none",boxSizing:"border-box"}}/>
              {resetError && (
                <div style={{fontSize:12,color:"#EF4444",fontWeight:700,marginTop:6}}>
                  ⚠️ {resetError}
                </div>
              )}
            </div>

            <button onClick={async()=>{
              const lockUntil = parseInt(localStorage.getItem("finvault_reset_lock")||"0");
              if (Date.now() < lockUntil) {
                const m = Math.ceil((lockUntil-Date.now())/60000);
                setResetError(`Reset locked. Try again in ${m} minute${m!==1?"s":""}.`);
                return;
              }
              setResetLoading(true); setResetError("");
              try {
                await resetPassword(resetEmail);
                setResetSent(true);
              } catch(e) { setResetError(e.message); }
              setResetLoading(false);
            }}
              disabled={resetLoading || Date.now() < parseInt(localStorage.getItem("finvault_reset_lock")||"0")}
              style={{width:"100%",padding:"15px",border:"none",borderRadius:14,
                cursor:"pointer",fontFamily:"inherit",fontWeight:900,fontSize:15,
                background:"linear-gradient(135deg,#4F46E5,#7C3AED)",color:"white",
                marginBottom:10,opacity:resetLoading?0.7:1}}>
              {resetLoading ? "Sending…" : "📧 Send Reset Link"}
            </button>

          </> : <>
            {/* Success screen */}
            <div style={{textAlign:"center",padding:"10px 0"}}>
              <div style={{fontSize:52,marginBottom:12}}>📬</div>
              <div style={{fontSize:16,fontWeight:900,color:"#16A34A",marginBottom:8}}>
                Reset Email Sent!
              </div>
              <div style={{fontSize:13,color:"#64748B",lineHeight:1.7,marginBottom:16}}>
                We sent a password reset link to:<br/>
                <strong style={{color:"#4F46E5"}}>{resetEmail}</strong>
              </div>
              <div style={{background:"#FEF3C7",border:"1.5px solid #FCD34D",borderRadius:12,
                padding:"10px 14px",fontSize:12,color:"#92400E",fontWeight:700,
                textAlign:"left",display:"flex",gap:8}}>
                <span>⚠️</span>
                <span>Check your <strong>Spam/Junk</strong> folder if you don't see it.</span>
              </div>
            </div>
          </>}
        </div>

        <button onClick={()=>{setShowForgot(false);setResetSent(false);setResetEmail("");setResetError("");}}
          style={{marginTop:20,background:"none",border:"none",color:"#94A3B8",
            fontSize:13,cursor:"pointer",fontFamily:"inherit",fontWeight:700}}>
          ← Back to Sign In
        </button>
      </div>
    );
  }

  // ── FEATURE 1: Email verification screen ────────────────────
  if (showVerify) {
    return (
      <div className="login-screen" data-theme={dark ? "dark" : "light"} style={{minHeight:"100vh",background:"linear-gradient(160deg,#EEF2FF,#F0FDF4)",
        display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
        padding:24,fontFamily:"'Nunito',sans-serif"}}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap');${darkThemeCss}`}</style>
        <div style={{fontSize:64,marginBottom:16}}>📧</div>
        <div style={{fontSize:22,fontWeight:900,color:"#1E1B4B",marginBottom:8,textAlign:"center"}}>
          Verify Your Email
        </div>
        <div style={{fontSize:14,color:"#64748B",marginBottom:8,textAlign:"center",maxWidth:320,lineHeight:1.7}}>
          We sent a verification link to:
        </div>
        <div style={{fontSize:15,fontWeight:800,color:"#4F46E5",marginBottom:24,textAlign:"center"}}>
          {email}
        </div>
        <div style={{background:"white",borderRadius:20,padding:20,width:"100%",maxWidth:360,
          boxShadow:"0 4px 24px rgba(0,0,0,0.08)",marginBottom:16}}>
          <div style={{fontSize:13,color:"#64748B",lineHeight:1.8,marginBottom:12}}>
            1. Open your email inbox<br/>
            2. Click the verification link<br/>
            3. Come back and tap "I've Verified" below
          </div>
          <div style={{background:"#FEF3C7",border:"1.5px solid #FCD34D",borderRadius:12,
            padding:"12px 14px",marginBottom:16,fontSize:12,color:"#92400E",fontWeight:800,
            display:"flex",alignItems:"flex-start",gap:8,lineHeight:1.55}}>
            <span style={{fontSize:16,flexShrink:0}}>⚠️</span>
            <span><strong>Didn't receive it?</strong><br/>Check Spam / Junk / Promotions folder, then mark the email as <strong>Not spam</strong>. Wait 1–2 minutes before using resend.</span>
          </div>
          <div style={{background:"#EEF2FF",border:"1.5px solid #C7D2FE",borderRadius:12,
            padding:"10px 14px",marginBottom:16,fontSize:12,color:"#4338CA",fontWeight:800,
            lineHeight:1.55}}>
            FinVault verification emails are sent from Firebase. If it lands in spam once, marking it as “Not spam” helps future emails arrive in Inbox.
          </div>
          <button onClick={async()=>{
            setVerifyChecking(true);
            const verified = await checkVerification(email, pass);
            setVerifyChecking(false);
            if (verified) setShowVerify(false);
            else setError("Email not verified yet. Please check Inbox, Spam/Junk/Promotions, and click the verification link.");
          }}
            style={{width:"100%",padding:15,background:"linear-gradient(135deg,#4F46E5,#7C3AED)",
              border:"none",borderRadius:14,cursor:"pointer",fontFamily:"inherit",
              fontWeight:900,fontSize:15,color:"white",marginBottom:10}}>
            {verifyChecking ? "Checking…" : "✅ I've Verified My Email"}
          </button>
          <button onClick={async()=>{
            try {
              await resendVerification(email, pass);
              setError(""); alert("✅ Verification email resent! Check Inbox first. If not found, check Spam/Junk/Promotions and mark it as Not spam.");
            } catch(e) { setError(e.message); }
          }}
            style={{width:"100%",padding:12,background:"#F1F5F9",border:"none",borderRadius:14,
              cursor:"pointer",fontFamily:"inherit",fontWeight:700,fontSize:13,color:"#64748B"}}>
            📨 Resend Verification Email
          </button>
        </div>
        {error && <div style={{fontSize:12,color:"#EF4444",fontWeight:700,textAlign:"center"}}>{error}</div>}
        <button onClick={()=>{setShowVerify(false);setMode("login");setPass("");setEmail("");}}
          style={{marginTop:12,background:"none",border:"none",color:"#94A3B8",
            fontSize:12,cursor:"pointer",fontFamily:"inherit",fontWeight:700}}>
          ← Back to Sign In
        </button>
      </div>
    );
  }

  return (
    <div className="login-screen" data-theme={dark ? "dark" : "light"} style={{
      minHeight:"100vh", background:"linear-gradient(160deg,#EEF2FF 0%,#F0FDF4 100%)",
      display:"flex", flexDirection:"column", alignItems:"center",
      justifyContent:"center", padding:"24px", fontFamily:"'Nunito',sans-serif"
    }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap');${darkThemeCss}`}</style>

      {/* Logo */}
      <img
        src="/icons/icon-192.png"
        alt="FinVault"
        width="84"
        height="84"
        style={{borderRadius:24,marginBottom:16,boxShadow:"0 14px 42px rgba(79,70,229,.28)",display:"block"}}
      />
      <div style={{fontSize:28,fontWeight:900,color:"#1E1B4B",letterSpacing:-1,marginBottom:4}}>FinVault</div>
      <div style={{fontSize:13,color:"#7C8DB5",marginBottom:16}}>Your Smart Money Manager</div>

      <div style={{background:"#EEF2FF",border:"1.5px solid #C7D2FE",borderRadius:16,padding:"10px 14px",marginBottom:16,width:"100%",maxWidth:380,boxSizing:"border-box"}}>
        <div style={{fontSize:12,fontWeight:900,color:"#4338CA",marginBottom:3}}>🔐 Safer login mode</div>
        <div style={{fontSize:11,color:"#64748B",fontWeight:800,lineHeight:1.45}}>Cloud login is session-based. After reinstall or a fresh app session, FinVault asks you to sign in again instead of silently opening your vault.</div>
      </div>

      {/* Lockout banner */}
      {lockedOut && (
        <div style={{background:"#FEF2F2",border:"2px solid #FECACA",borderRadius:14,
          padding:"12px 16px",marginBottom:16,width:"100%",maxWidth:380,textAlign:"center"}}>
          <div style={{fontSize:20,marginBottom:4}}>🔒</div>
          <div style={{fontSize:13,fontWeight:800,color:"#DC2626"}}>Email Temporarily Locked</div>
          <div style={{fontSize:12,color:"#EF4444",marginTop:4}}>Too many failed attempts for this email. Try another account or try again in {lockMins} minute{lockMins!==1?"s":""}.</div>
        </div>
      )}

      {/* Card */}
      <div style={{background:"white",borderRadius:24,padding:"28px 24px",width:"100%",maxWidth:380,boxShadow:"0 4px 32px rgba(0,0,0,0.08)"}}>
        {/* Mode tabs */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,background:"#F1F5F9",borderRadius:14,padding:4,marginBottom:24}}>
          {["login","register"].map(m=>(
            <button key={m} onClick={()=>{
              setMode(m); setError(""); setPass(""); setConfirm("");
              setEmail(""); setName(""); setShowPass(false);
            }}
              style={{padding:"10px",border:"none",borderRadius:10,cursor:"pointer",fontFamily:"inherit",fontWeight:800,fontSize:13,
                background:mode===m?"white":"transparent",color:mode===m?"#4F46E5":"#94A3B8",
                boxShadow:mode===m?"0 2px 8px rgba(0,0,0,0.08)":"none",transition:"all 0.2s"}}>
              {m==="login"?"Sign In":"Create Account"}
            </button>
          ))}
        </div>

        {/* Google */}
        <button onClick={handleGoogle} disabled={loading}
          style={{width:"100%",padding:"13px",border:"2px solid #E2E8F0",borderRadius:14,background:"white",cursor:"pointer",fontFamily:"inherit",fontWeight:800,fontSize:14,color:"#1E1B4B",display:"flex",alignItems:"center",justifyContent:"center",gap:10,marginBottom:20,opacity:1}}>
          <svg width="18" height="18" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </button>

        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:20}}>
          <div style={{flex:1,height:1,background:"#E2E8F0"}}/>
          <span style={{fontSize:12,color:"#94A3B8",fontWeight:700}}>OR</span>
          <div style={{flex:1,height:1,background:"#E2E8F0"}}/>
        </div>

        {mode==="register" && (
          <div style={{marginBottom:12}}>
            <div style={{fontSize:11,fontWeight:800,color:"#94A3B8",letterSpacing:1,textTransform:"uppercase",marginBottom:6}}>Your Name</div>
            <input placeholder="Full Name" value={name} onChange={e=>setName(sanitizeProfileNameInput(e.target.value))}
              style={{width:"100%",padding:"13px 16px",borderRadius:12,border:"2px solid #E8EDFF",background:"#F8FAFF",fontSize:14,fontFamily:"inherit",fontWeight:600,color:"#1E1B4B",outline:"none",boxSizing:"border-box"}}/>
          </div>
        )}

        {/* Username field — register only */}
        {mode==="register" && (
          <div style={{marginBottom:12}}>
            <div style={{fontSize:11,fontWeight:800,color:"#94A3B8",letterSpacing:1,textTransform:"uppercase",marginBottom:6}}>Username</div>
            <div style={{position:"relative"}}>
              <span style={{position:"absolute",left:14,top:"50%",transform:"translateY(-50%)",fontSize:14,color:"#94A3B8",fontWeight:700}}>@</span>
              <input placeholder="unique_username" value={username}
                onChange={e=>{
                  const val = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g,"");
                  setUsername(val);
                  setUsernameError(validateUsername(val));
                }}
                style={{width:"100%",padding:"13px 16px 13px 32px",borderRadius:12,
                  border:`2px solid ${usernameError && username.length>0?"#EF4444":username.length>=3?"#22C55E":"#E8EDFF"}`,
                  background:"#F8FAFF",fontSize:14,fontFamily:"inherit",fontWeight:600,
                  color:"#1E1B4B",outline:"none",boxSizing:"border-box"}}/>
              {username.length >= 3 && !usernameError && (
                <span style={{position:"absolute",right:14,top:"50%",transform:"translateY(-50%)",fontSize:16}}>✅</span>
              )}
            </div>
            {usernameError && username.length>0 && (
              <div style={{fontSize:11,color:"#EF4444",fontWeight:700,marginTop:4}}>⚠️ {usernameError}</div>
            )}
            <div style={{fontSize:10,color:"#94A3B8",marginTop:4}}>Letters, numbers, underscores only. 3–20 chars.</div>
          </div>
        )}

        <div style={{marginBottom:12}}>
          <div style={{fontSize:11,fontWeight:800,color:"#94A3B8",letterSpacing:1,textTransform:"uppercase",marginBottom:6}}>Email</div>
          <input type="email" placeholder="you@email.com" value={email} onChange={e=>setEmail(sanitizeEmail(e.target.value))}
            style={{width:"100%",padding:"13px 16px",borderRadius:12,border:"2px solid #E8EDFF",background:"#F8FAFF",fontSize:14,fontFamily:"inherit",fontWeight:600,color:"#1E1B4B",outline:"none",boxSizing:"border-box"}}/>
        </div>

        {/* Password field */}
        <div style={{marginBottom: mode==="register" ? 12 : 20}}>
          <div style={{fontSize:11,fontWeight:800,color:"#94A3B8",letterSpacing:1,textTransform:"uppercase",marginBottom:6}}>Password</div>
          <div style={{position:"relative"}}>
            <input
              type={showPass ? "text" : "password"}
              placeholder="••••••••"
              value={pass}
              onChange={e=>setPass(e.target.value)}
              onKeyDown={e=>e.key==="Enter"&&handle()}
              style={{width:"100%",padding:"13px 48px 13px 16px",borderRadius:12,
                border:`2px solid ${mode==="register" && pass.length>0 ? strength.color : "#E8EDFF"}`,
                background:"#F8FAFF",fontSize:14,fontFamily:"inherit",fontWeight:600,
                color:"#1E1B4B",outline:"none",boxSizing:"border-box",transition:"border-color 0.2s"}}/>
            <button onClick={()=>setShowPass(s=>!s)}
              style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",
                background:"none",border:"none",cursor:"pointer",fontSize:16,padding:4,color:"#94A3B8"}}>
              {showPass ? "🙈" : "👁️"}
            </button>
          </div>

          {/* Strength bar */}
          {mode==="register" && pass.length > 0 && (
            <div style={{marginTop:8}}>
              <div style={{display:"flex",gap:4,marginBottom:4}}>
                {[1,2,3,4,5].map(i=>(
                  <div key={i} style={{flex:1,height:4,borderRadius:999,
                    background: i <= strength.score ? strength.color : "#E2E8F0",
                    transition:"background 0.25s"}}/>
                ))}
              </div>
              <div style={{fontSize:11,fontWeight:800,color:strength.color,textAlign:"right"}}>
                {strength.label}
              </div>
            </div>
          )}

          {/* Rules checklist */}
          {mode==="register" && (
            <div style={{marginTop:10,background:"#F8FAFF",borderRadius:12,padding:"10px 12px",border:"1.5px solid #E8EDFF"}}>
              {rulesDone.map(r=>(
                <div key={r.id} style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                  <div style={{width:16,height:16,borderRadius:"50%",flexShrink:0,
                    background:r.passed?"#DCFCE7":"#F1F5F9",
                    border:`2px solid ${r.passed?"#22C55E":"#E2E8F0"}`,
                    display:"flex",alignItems:"center",justifyContent:"center",
                    fontSize:9,transition:"all 0.2s"}}>
                    {r.passed && <span style={{color:"#16A34A",fontWeight:900}}>✓</span>}
                  </div>
                  <span style={{fontSize:11,fontWeight:700,color:r.passed?"#16A34A":"#94A3B8",transition:"color 0.2s"}}>
                    {r.label}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Confirm password */}
        {mode==="register" && (
          <div style={{marginBottom:20}}>
            <div style={{fontSize:11,fontWeight:800,color:"#94A3B8",letterSpacing:1,textTransform:"uppercase",marginBottom:6}}>Confirm Password</div>
            <div style={{position:"relative"}}>
              <input
                type={showPass ? "text" : "password"}
                placeholder="Re-enter password"
                value={confirm}
                onChange={e=>setConfirm(e.target.value)}
                onKeyDown={e=>e.key==="Enter"&&handle()}
                style={{width:"100%",padding:"13px 48px 13px 16px",borderRadius:12,
                  border:`2px solid ${confirm.length>0?(confirm===pass?"#22C55E":"#EF4444"):"#E8EDFF"}`,
                  background:"#F8FAFF",fontSize:14,fontFamily:"inherit",fontWeight:600,
                  color:"#1E1B4B",outline:"none",boxSizing:"border-box",transition:"border-color 0.2s"}}/>
              {confirm.length > 0 && (
                <div style={{position:"absolute",right:14,top:"50%",transform:"translateY(-50%)",fontSize:16}}>
                  {confirm===pass ? "✅" : "❌"}
                </div>
              )}
            </div>
            {confirm.length>0 && confirm!==pass && (
              <div style={{fontSize:11,color:"#EF4444",fontWeight:700,marginTop:5}}>Passwords don't match</div>
            )}
          </div>
        )}

        {error && (
          <div style={{background:"#FEF2F2",border:"1.5px solid #FECACA",borderRadius:12,padding:"10px 14px",marginBottom:16,fontSize:13,color:"#DC2626",fontWeight:700}}>
            ⚠️ {error}
          </div>
        )}

        <button onClick={handle} disabled={loading}
          style={{width:"100%",padding:"15px",border:"none",borderRadius:14,cursor:"pointer",fontFamily:"inherit",fontWeight:900,fontSize:16,
            background:"linear-gradient(135deg,#4F46E5,#7C3AED)",color:"white",opacity:(loading||lockedOut)?0.7:1}}>
          {loading ? "Please wait…" : mode==="login" ? "Sign In →" : "Create Account →"}
        </button>

        {/* Forgot Password link — login mode only */}
        {mode==="login" && (
          <button onClick={()=>{setShowForgot(true);setResetEmail(email);}}
            style={{width:"100%",marginTop:10,background:"none",border:"none",
              color:"#4F46E5",fontSize:13,cursor:"pointer",fontFamily:"inherit",
              fontWeight:700,textDecoration:"none",padding:"8px"}}>
            🔑 Forgot Password?
          </button>
        )}
      </div>

      <div style={{background:"#FFF7ED",border:"1.5px solid #FED7AA",borderRadius:16,padding:"11px 14px",marginTop:16,width:"100%",maxWidth:380,boxSizing:"border-box"}}>
        <div style={{fontSize:12,fontWeight:900,color:"#C2410C",marginBottom:3}}>🛡️ Scam safety reminder</div>
        <div style={{fontSize:11,color:"#64748B",fontWeight:800,lineHeight:1.45}}>FinVault never asks for OTP, UPI PIN, ATM PIN, card PIN, CVV, or bank password. Do not share backup files with unknown people.</div>
      </div>


      <div style={{fontSize:12,color:"#94A3B8",marginTop:20,textAlign:"center",maxWidth:320,lineHeight:1.6}}>
        🔒 Your data is protected. FinVault never asks for OTP, UPI PIN, CVV, ATM PIN, or bank password.
        <br/>
        By continuing, you agree to the{" "}
        <button type="button" onClick={()=>openInternalPage("/terms")} style={{background:"none",border:"none",padding:0,color:"#4F46E5",fontWeight:900,textDecoration:"none",fontFamily:"inherit",fontSize:"inherit",cursor:"pointer"}}>Terms & Conditions</button>
        {" "}and{" "}
        <button type="button" onClick={()=>openInternalPage("/privacy")} style={{background:"none",border:"none",padding:0,color:"#4F46E5",fontWeight:900,textDecoration:"none",fontFamily:"inherit",fontSize:"inherit",cursor:"pointer"}}>Privacy Policy</button>.
      </div>
      {onChangeStorage && (
        <button onClick={onChangeStorage}
          style={{marginTop:12,background:"none",border:"none",color:"#94A3B8",fontSize:12,cursor:"pointer",fontFamily:"inherit",fontWeight:700,textDecoration:"underline"}}>
          ← Change storage option
        </button>
      )}
    </div>
  );
}
